import React, { useMemo } from 'react';
import { Flex } from '@app/ui';
import connect from '@app/utils/connect';
import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import { User } from '@app/types/api';
import { useBroadcastsContext } from '../../BroadcastsContext';
import useBroadcastDemographics from './useBroadcastDemographics';
import BroadcastDemographicsTable from './BroadcastDemographicsTable';
import { aggregateDemographics } from './demographicsUtils';

type BroadcastDemographicsProps = {
    // The CSV delimiter is a user setting (Settings › Region), not a client
    // attribute, hence the store connection rather than the broadcasts context.
    user?: Pick<User, 'csvDelimiter'> | null;
};

// Demographics Breakdown tab. Reads the shared Broadcast Exposure
// filters plus the tab's own Age Bracket / Gender selection.
const BroadcastDemographics = ({ user }: BroadcastDemographicsProps) => {
    const { client, broadcastsQuery, demographicFilters, filters } =
        useBroadcastsContext();

    // `isInitialLoading` rather than `isFetching`: a background refetch (if one
    // is ever re-enabled) must not replace rows on screen with a skeleton.
    const { data, isInitialLoading } = useBroadcastDemographics(
        client?.id,
        broadcastsQuery,
        demographicFilters,
    );

    const rows = useMemo(() => aggregateDemographics(data), [data]);

    return (
        <ErrorBoundary FallbackComponent={CrashedComponent}>
            <Flex
                vertical
                data-test="broadcast-demographics-container"
                style={{ width: '100%' }}
            >
                <BroadcastDemographicsTable
                    rows={rows}
                    csvDelimiter={user?.csvDelimiter}
                    loading={isInitialLoading}
                    filters={filters}
                />
            </Flex>
        </ErrorBoundary>
    );
};

export default connect(['user'])(BroadcastDemographics);
